import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  IconButton,
  InputAdornment,
  Alert,
  Chip,
  CircularProgress,
  SelectChangeEvent,
  Tooltip
} from '@mui/material'
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material'
import { inspectorApi, XeroContact, XeroContactsResponse } from '../services/api'
import { useDebounce } from '../hooks/useDebounce'

interface XeroContactsTableProps {
  fpCode: string
}

const XeroContactsTable: React.FC<XeroContactsTableProps> = ({ fpCode }) => {
  const [contacts, setContacts] = useState<XeroContact[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)
  const [totalCount, setTotalCount] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [contactType, setContactType] = useState<'all' | 'customers' | 'suppliers'>('all')
  const [sortBy, setSortBy] = useState<'name' | 'email' | 'updated_at'>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null)

  const debouncedSearchTerm = useDebounce(searchTerm, 500)

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await inspectorApi.getXeroContacts(fpCode, {
        page,
        page_size: pageSize,
        search: debouncedSearchTerm || undefined,
        contact_type: contactType,
        sort_by: sortBy,
        sort_order: sortOrder
      })
      
      const data: XeroContactsResponse = response.data
      setContacts(data.contacts)
      setTotalCount(data.total_count)
      setLastSyncAt(data.last_sync_at || null)
    } catch (err: any) {
      console.error('❌ Error fetching Xero contacts:', err)
      setError(err.response?.data?.detail || 'Failed to load contacts')
    } finally {
      setLoading(false)
    }
  }, [fpCode, page, pageSize, debouncedSearchTerm, contactType, sortBy, sortOrder])

  useEffect(() => {
    fetchContacts()
  }, [fetchContacts])

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  const handleContactTypeChange = (event: SelectChangeEvent<'all' | 'customers' | 'suppliers'>) => {
    setContactType(event.target.value as 'all' | 'customers' | 'suppliers')
    setPage(1)
  }

  const handleSortChange = (newSortBy: 'name' | 'email' | 'updated_at') => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(newSortBy)
      setSortOrder('asc')
    }
    setPage(1)
  }

  const clearSearch = () => {
    setSearchTerm('')
    setPage(1)
  }

  const formatAddress = (address?: XeroContact['address']) => {
    if (!address) return null
    const parts = [
      address.line1,
      address.line2,
      address.city,
      address.region,
      address.postal_code,
      address.country
    ].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : null
  }

  const totalPages = Math.ceil(totalCount / pageSize)

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    )
  }

  return (
    <Box>
      {/* Header with last sync info */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          🏢 Xero Contacts
        </Typography>
        {lastSyncAt && (
          <Typography variant="body2" color="text.secondary">
            Last synced: {new Date(lastSyncAt).toLocaleString()}
          </Typography>
        )}
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton onClick={clearSearch} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Contact Type</InputLabel>
          <Select
            value={contactType}
            onChange={handleContactTypeChange}
            label="Contact Type"
          >
            <MenuItem value="all">All Contacts</MenuItem>
            <MenuItem value="customers">Clients Only</MenuItem>
            <MenuItem value="suppliers">Suppliers Only</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing {contacts.length} of {totalCount} contacts
      </Typography>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell 
                onClick={() => handleSortChange('name')}
                sx={{ cursor: 'pointer', fontWeight: 'bold' }}
              >
                Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell>Type</TableCell>
              <TableCell 
                onClick={() => handleSortChange('email')}
                sx={{ cursor: 'pointer' }}
              >
                Email {sortBy === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Address</TableCell>
              <TableCell 
                onClick={() => handleSortChange('updated_at')}
                sx={{ cursor: 'pointer' }}
              >
                Last Updated {sortBy === 'updated_at' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : contacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No contacts found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              contacts.map((contact) => (
                <TableRow key={contact.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {contact.name}
                      </Typography>
                      {contact.contact_number && (
                        <Typography variant="caption" color="text.secondary">
                          #{contact.contact_number}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {contact.is_customer && (
                        <Chip
                          icon={<PersonIcon />}
                          label="Client"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {contact.is_supplier && (
                        <Chip
                          icon={<BusinessIcon />}
                          label="Supplier"
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {contact.email_address && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2">{contact.email_address}</Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {contact.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2">{contact.phone}</Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {formatAddress(contact.address) && (
                      <Tooltip title={formatAddress(contact.address) || ''}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {formatAddress(contact.address)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(contact.updated_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
    </Box>
  )
}

export default XeroContactsTable